import React, { useState, useEffect } from "react";
import { getAllPlans } from "@api/subscription/plan";
import {
  createOnboardingSubscription,
  handleFreePlanSubscription,
} from "@api/subscription/stripe";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "@assets/images/logo/logo-light-nobg.png";

// Local storage key
const SUBSCRIPTION_COMPLETE_KEY = "copilotly_subscription_complete";

// Inline styles for the entire component - Premium Dark Theme (Compact)
const styles = {
  // Overlay
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0, 0, 0, 0.85)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px",
  },

  // Modal Container
  modal: {
    width: "100%",
    maxWidth: "1100px",
    maxHeight: "calc(100vh - 20px)",
    background: "linear-gradient(180deg, #0d0d1a 0%, #12121f 50%, #0d0d1a 100%)",
    borderRadius: "20px",
    boxShadow: "0 50px 100px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.08)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
  },

  // Ambient glow effect
  ambientGlow: {
    position: "absolute",
    top: "-200px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "600px",
    height: "400px",
    background: "radial-gradient(ellipse, rgba(123, 97, 255, 0.12) 0%, transparent 70%)",
    pointerEvents: "none",
  },

  // Header - Compact
  header: {
    padding: "16px 24px",
    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px",
    position: "relative",
    zIndex: 1,
  },

  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  logo: {
    height: "32px",
    width: "auto",
  },

  headerTitle: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "-0.5px",
  },

  // Billing Toggle - Compact
  billingToggle: {
    display: "flex",
    gap: "3px",
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    padding: "3px",
    borderRadius: "10px",
  },

  billingBtn: {
    padding: "8px 14px",
    border: "none",
    borderRadius: "7px",
    background: "transparent",
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    whiteSpace: "nowrap",
  },

  billingBtnActive: {
    background: "rgba(123, 97, 255, 0.2)",
    color: "#ffffff",
  },

  billingBadge: {
    fontSize: "8px",
    padding: "2px 6px",
    borderRadius: "100px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  badgeTry: {
    background: "rgba(255, 255, 255, 0.1)",
    color: "rgba(255, 255, 255, 0.6)",
  },

  badgeSave: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "#ffffff",
  },

  // Content area - Compact
  content: {
    flex: 1,
    overflow: "auto",
    padding: "16px 24px",
    position: "relative",
    zIndex: 1,
  },

  // Tab Navigation - Compact
  tabNav: {
    display: "flex",
    justifyContent: "center",
    gap: "6px",
    marginBottom: "16px",
  },

  tabBtn: {
    padding: "8px 18px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "8px",
    background: "transparent",
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  tabBtnActive: {
    background: "rgba(123, 97, 255, 0.15)",
    borderColor: "rgba(123, 97, 255, 0.3)",
    color: "#ffffff",
  },

  // Plans Grid - Compact
  plansGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
    maxWidth: "1050px",
    margin: "0 auto",
  },

  // Plan Card - Compact
  planCard: {
    background: "linear-gradient(180deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "16px",
    padding: "0",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
  },

  planCardHover: {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 32px rgba(0, 0, 0, 0.4)",
    borderColor: "rgba(255, 255, 255, 0.15)",
  },

  planCardSelected: {
    borderColor: "rgba(123, 97, 255, 0.5)",
    boxShadow: "0 0 0 1px rgba(123, 97, 255, 0.5), 0 16px 32px rgba(123, 97, 255, 0.15)",
  },

  planCardPopular: {
    background: "linear-gradient(180deg, rgba(123, 97, 255, 0.08) 0%, rgba(123, 97, 255, 0.02) 100%)",
    border: "1px solid rgba(123, 97, 255, 0.3)",
    transform: "scale(1.02)",
    zIndex: 1,
  },

  // Popular Badge - Clean ribbon at top
  popularBadge: {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    background: "linear-gradient(135deg, #7B61FF 0%, #A78BFA 100%)",
    color: "#ffffff",
    padding: "6px 16px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    textAlign: "center",
    borderRadius: "16px 16px 0 0",
  },

  // Plan Header - Compact
  planHeader: {
    padding: "16px 16px 10px",
    textAlign: "center",
  },

  planIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 10px",
    fontSize: "18px",
    background: "rgba(123, 97, 255, 0.12)",
    border: "1px solid rgba(123, 97, 255, 0.2)",
    color: "#A78BFA",
  },

  planIconPopular: {
    background: "linear-gradient(135deg, #7B61FF 0%, #A78BFA 100%)",
    color: "#ffffff",
    border: "none",
    boxShadow: "0 6px 16px rgba(123, 97, 255, 0.4)",
  },

  planName: {
    margin: "0",
    fontSize: "16px",
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "-0.3px",
  },

  // Plan Price - Compact
  planPriceSection: {
    padding: "0 16px 12px",
    textAlign: "center",
  },

  priceAmount: {
    fontSize: "36px",
    fontWeight: 800,
    color: "#ffffff",
    letterSpacing: "-2px",
    lineHeight: 1,
    margin: 0,
  },

  priceAmountPopular: {
    background: "linear-gradient(135deg, #C4B5FD 0%, #A78BFA 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },

  pricePeriod: {
    fontSize: "12px",
    color: "rgba(255, 255, 255, 0.5)",
    marginTop: "4px",
  },

  priceSaveNote: {
    fontSize: "10px",
    color: "#10b981",
    marginTop: "2px",
  },

  // Divider
  divider: {
    height: "1px",
    background: "rgba(255, 255, 255, 0.06)",
    margin: "0 16px",
  },

  // Features List - Compact
  featuresList: {
    padding: "12px 16px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxHeight: "140px",
    overflowY: "auto",
  },

  featureItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.8)",
    lineHeight: 1.3,
  },

  featureCheck: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    background: "rgba(16, 185, 129, 0.15)",
    color: "#10b981",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: "8px",
    marginTop: "1px",
  },

  // CTA Button - Compact
  ctaSection: {
    padding: "12px 16px 16px",
    marginTop: "auto",
    borderTop: "1px solid rgba(255, 255, 255, 0.06)",
  },

  ctaButton: {
    width: "100%",
    padding: "10px 16px",
    border: "none",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  ctaFree: {
    background: "transparent",
    border: "2px solid #10b981",
    color: "#10b981",
  },

  ctaPrimary: {
    background: "linear-gradient(135deg, #7B61FF 0%, #A78BFA 100%)",
    color: "#ffffff",
    boxShadow: "0 4px 12px rgba(123, 97, 255, 0.3)",
  },

  ctaEnterprise: {
    background: "transparent",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    color: "rgba(255, 255, 255, 0.8)",
  },

  // Guarantees - Compact
  guarantees: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginTop: "16px",
    flexWrap: "wrap",
  },

  guarantee: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.6)",
  },

  guaranteeDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
  },

  // Footer - Compact
  footer: {
    padding: "12px 24px",
    borderTop: "1px solid rgba(255, 255, 255, 0.06)",
    display: "flex",
    justifyContent: "center",
    position: "relative",
    zIndex: 1,
  },

  laterBtn: {
    padding: "10px 20px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "8px",
    background: "transparent",
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  // Loading
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 0",
    gap: "12px",
  },

  spinner: {
    width: "32px",
    height: "32px",
    border: "3px solid rgba(255, 255, 255, 0.1)",
    borderTop: "3px solid #7B61FF",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  // Comparison Tab - Compact
  comparisonTable: {
    background: "rgba(255, 255, 255, 0.02)",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    overflow: "hidden",
    maxWidth: "950px",
    margin: "0 auto",
  },

  comparisonHeader: {
    display: "grid",
    gridTemplateColumns: "160px repeat(5, 1fr)",
    background: "rgba(123, 97, 255, 0.1)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
  },

  comparisonHeaderCell: {
    padding: "10px 8px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#ffffff",
    textAlign: "center",
  },

  comparisonRow: {
    display: "grid",
    gridTemplateColumns: "160px repeat(5, 1fr)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
    transition: "background 0.2s ease",
  },

  comparisonCell: {
    padding: "10px 8px",
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.7)",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  comparisonFeatureCell: {
    justifyContent: "flex-start",
    paddingLeft: "16px",
    fontWeight: 500,
    color: "rgba(255, 255, 255, 0.9)",
  },

  comparisonCategory: {
    fontSize: "10px",
    fontWeight: 600,
    color: "#A78BFA",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },

  checkIcon: {
    color: "#10b981",
    fontSize: "14px",
  },

  crossIcon: {
    color: "rgba(255, 255, 255, 0.2)",
    fontSize: "12px",
  },

  // Testimonials - Compact
  testimonialsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "16px",
    maxWidth: "950px",
    margin: "0 auto",
  },

  testimonialCard: {
    background: "rgba(255, 255, 255, 0.03)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "12px",
    padding: "20px",
    transition: "all 0.3s ease",
  },

  testimonialQuote: {
    fontSize: "28px",
    color: "#7B61FF",
    lineHeight: 1,
    marginBottom: "10px",
  },

  testimonialText: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: "rgba(255, 255, 255, 0.8)",
    marginBottom: "16px",
  },

  testimonialHighlight: {
    fontWeight: 700,
    color: "#A78BFA",
  },

  testimonialAuthor: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    borderTop: "1px solid rgba(255, 255, 255, 0.08)",
    paddingTop: "14px",
  },

  testimonialAvatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "12px",
  },

  testimonialName: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#ffffff",
    marginBottom: "1px",
  },

  testimonialRole: {
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.5)",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "14px",
    marginBottom: "24px",
    maxWidth: "700px",
    margin: "0 auto 24px",
  },

  statCard: {
    padding: "20px",
    borderRadius: "12px",
    textAlign: "center",
    color: "#ffffff",
  },

  statNumber: {
    fontSize: "28px",
    fontWeight: 800,
    marginBottom: "4px",
    letterSpacing: "-1px",
  },

  statLabel: {
    fontSize: "11px",
    opacity: 0.9,
  },
};

// Icon components (SVG)
const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
    <path d="M8.5 2.5L3.75 7.25L1.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const StarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const RocketIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/>
    <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/>
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
  </svg>
);

const BoltIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
  </svg>
);

const CrownIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 14h14v2H5v-2z"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const getPlanIcon = (planName) => {
  switch (planName) {
    case "Free": return <ShieldIcon />;
    case "Starter": return <RocketIcon />;
    case "Pro": return <BoltIcon />;
    case "Business": return <CrownIcon />;
    case "Enterprise": return <CrownIcon />;
    default: return <StarIcon />;
  }
};

const SubscriptionModal = ({ visible, onClose, user }) => {
  const history = useHistory();
  const { user: reduxUser } = useSelector((state) => state.auth);
  const currentUser = user || reduxUser;

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState("month");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscribingPlan, setSubscribingPlan] = useState(null);
  const [activeTab, setActiveTab] = useState("plans");
  const [hoveredCard, setHoveredCard] = useState(null);

  // Check local storage on mount
  useEffect(() => {
    if (localStorage.getItem(SUBSCRIPTION_COMPLETE_KEY) === "true") {
      onClose();
    }
  }, [onClose]);

  // Fetch plans on mount
  useEffect(() => {
    if (visible && !localStorage.getItem(SUBSCRIPTION_COMPLETE_KEY)) {
      setLoading(true);
      getAllPlans()
        .then((res) => {
          const plansData = res.data.plans || res.data || [];
          const planOrder = ["Free", "Starter", "Pro", "Business", "Enterprise"];

          const sortedPlans = Array.isArray(plansData)
            ? plansData.sort((a, b) => {
                const orderA = planOrder.indexOf(a.name);
                const orderB = planOrder.indexOf(b.name);
                return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
              })
            : [];

          setPlans(
            sortedPlans.map((plan) => {
              const enhancedPlan = { ...plan };
              if (!enhancedPlan.features) enhancedPlan.features = {};

              if (!enhancedPlan.featureList) {
                if (Array.isArray(plan.features)) {
                  enhancedPlan.featureList = plan.features.map(f => {
                    if (typeof f === 'string') return f;
                    if (typeof f === 'object' && f.name) return f.name;
                    if (typeof f === 'object') {
                      const chars = [];
                      let i = 0;
                      while (f[i] !== undefined) {
                        chars.push(f[i]);
                        i++;
                      }
                      return chars.join('');
                    }
                    return '';
                  }).filter(Boolean);
                } else {
                  enhancedPlan.featureList = [];
                }
              }
              return enhancedPlan;
            })
          );

          const wizardPlan = sortedPlans.find((p) => p.name === "Wizard") || sortedPlans[2];
          if (wizardPlan) setSelectedPlan(wizardPlan._id);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error loading plans:", err);
          setLoading(false);
        });
    }
  }, [visible]);

  // Format price
  const formatPrice = (plan) => {
    if (plan.name === "Free") return "$0";
    if (plan.name === "Enterprise") return "Custom";

    let price;
    if (billingInterval === "week") {
      price = plan.weeklyPrice || Math.round(plan.monthlyPrice / 4);
    } else if (billingInterval === "year") {
      price = plan.annualPrice;
    } else {
      price = plan.monthlyPrice;
    }

    if (!price) return "$0";
    return `$${(price / 100).toFixed(0)}`;
  };

  const getPeriodText = () => {
    if (billingInterval === "week") return "week";
    if (billingInterval === "year") return "year";
    return "month";
  };

  const isPopularPlan = (plan) => plan.name === "Wizard";

  const markSubscriptionComplete = () => {
    localStorage.setItem(SUBSCRIPTION_COMPLETE_KEY, "true");
  };

  const subscribeFree = async (plan) => {
    setSubscribingPlan(plan._id);
    try {
      await handleFreePlanSubscription();
      markSubscriptionComplete();
      setSubscribingPlan(null);
      onClose();
      if (currentUser?.workspaceId) {
        history.push(`/a/ws/${currentUser.workspaceId}/dashboard`);
      }
    } catch (error) {
      console.error("Free plan subscription error:", error);
      setSubscribingPlan(null);
      alert("There was an error activating your plan. Please try again.");
    }
  };

  const subscribePaid = async (plan) => {
    setSubscribingPlan(plan._id);
    try {
      let basePricingId, meteredPricingId;

      // Support both nested (plan.stripe.x) and flat (plan.stripeX) field structures
      if (billingInterval === "week") {
        basePricingId = plan.stripe?.weeklyPriceId || plan.stripeWeeklyPriceId;
        meteredPricingId = plan.stripe?.weeklyMeteredPriceId || plan.stripeWeeklyMeteredPriceId;
      } else if (billingInterval === "year") {
        basePricingId = plan.stripe?.annualPriceId || plan.stripeAnnualPriceId;
        meteredPricingId = plan.stripe?.annualMeteredPriceId || plan.stripeAnnualMeteredPriceId;
      } else {
        basePricingId = plan.stripe?.monthlyPriceId || plan.stripeMonthlyPriceId;
        meteredPricingId = plan.stripe?.monthlyMeteredPriceId || plan.stripeMonthlyMeteredPriceId;
      }

      if (!basePricingId) throw new Error("Price ID not available");

      // Only include meteredPricingId if it exists
      const payload = { basePricingId };
      if (meteredPricingId) {
        payload.meteredPricingId = meteredPricingId;
      }

      const res = await createOnboardingSubscription(payload);

      if (res.data.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setSubscribingPlan(null);
      localStorage.removeItem(SUBSCRIPTION_COMPLETE_KEY);
    }
  };

  const handleSubscribe = (plan) => {
    if (plan.name === "Free") {
      subscribeFree(plan);
    } else if (plan.name === "Enterprise") {
      markSubscriptionComplete();
      window.location.href = "mailto:support@copilotly.com?subject=Enterprise Plan Inquiry";
    } else {
      subscribePaid(plan);
    }
  };

  const handleDecideLater = () => {
    const freePlan = plans.find((p) => p.name === "Free");
    if (freePlan) {
      subscribeFree(freePlan);
    } else {
      markSubscriptionComplete();
      onClose();
      if (currentUser?.workspaceId) {
        history.push(`/a/ws/${currentUser.workspaceId}/dashboard`);
      }
    }
  };

  if (!visible || localStorage.getItem(SUBSCRIPTION_COMPLETE_KEY) === "true") {
    return null;
  }

  // Comparison data
  const comparisonData = [
    { category: "Usage", features: [
      { name: "Credits / Month", values: ["100", "1,000", "5,000", "Unlimited", "Custom"] },
      { name: "AI Models", values: ["GPT-3.5", "GPT-4, Claude", "All Models", "All Models", "Custom"] },
    ]},
    { category: "Features", features: [
      { name: "AI Search", values: [true, true, true, true, true] },
      { name: "AI Writing", values: [true, true, true, true, true] },
      { name: "Email Assistant", values: [false, true, true, true, true] },
      { name: "Chat History", values: [false, true, true, true, true] },
    ]},
    { category: "Support", features: [
      { name: "Support Level", values: ["Community", "Email", "Priority", "Dedicated", "White Glove"] },
      { name: "Priority Response", values: [false, false, true, true, true] },
    ]},
  ];

  const testimonials = [
    {
      text: "Copilotly has transformed how I work. The AI copilots save me hours every day on emails and research.",
      highlight: "hours every day",
      author: "Michael Kim",
      role: "Product Manager, TechCorp",
      initials: "MK",
      gradient: "linear-gradient(135deg, #7B61FF 0%, #A78BFA 100%)",
    },
    {
      text: "The legal copilot is incredible. It helps me draft contracts and review documents with professional accuracy.",
      highlight: "professional accuracy",
      author: "Sarah Johnson",
      role: "Attorney, Legal Partners LLP",
      initials: "SJ",
      gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    },
    {
      text: "As a developer, the code copilot is my secret weapon. It doubled my coding speed and catches bugs I miss.",
      highlight: "doubled my coding speed",
      author: "Alex Lee",
      role: "Senior Developer, StartupXYZ",
      initials: "AL",
      gradient: "linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)",
    },
  ];

  return (
    <div style={styles.overlay}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .plan-card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
            border-color: rgba(255, 255, 255, 0.15);
          }
          .cta-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(123, 97, 255, 0.4);
          }
          .billing-btn:hover {
            color: rgba(255, 255, 255, 0.8);
          }
          .later-btn:hover {
            color: rgba(255, 255, 255, 0.7);
            background: rgba(255, 255, 255, 0.05);
            border-color: rgba(255, 255, 255, 0.15);
          }
          .tab-btn:hover {
            color: rgba(255, 255, 255, 0.7);
            background: rgba(255, 255, 255, 0.03);
          }
          .comparison-row:hover {
            background: rgba(123, 97, 255, 0.05);
          }
          .testimonial-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 16px 32px rgba(0, 0, 0, 0.3);
            border-color: rgba(255, 255, 255, 0.12);
          }
          .features-scroll::-webkit-scrollbar {
            width: 4px;
          }
          .features-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .features-scroll::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 4px;
          }
        `}
      </style>

      <div style={styles.modal}>
        {/* Ambient Glow */}
        <div style={styles.ambientGlow} />

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <img src={logo} alt="Copilotly" style={styles.logo} />
            <h2 style={styles.headerTitle}>Choose Your Plan</h2>
          </div>

          <div style={styles.billingToggle}>
            <button
              className="billing-btn"
              style={{
                ...styles.billingBtn,
                ...(billingInterval === "week" ? styles.billingBtnActive : {}),
              }}
              onClick={() => setBillingInterval("week")}
            >
              Weekly
              <span style={{ ...styles.billingBadge, ...styles.badgeTry }}>Try it</span>
            </button>
            <button
              className="billing-btn"
              style={{
                ...styles.billingBtn,
                ...(billingInterval === "month" ? styles.billingBtnActive : {}),
              }}
              onClick={() => setBillingInterval("month")}
            >
              Monthly
            </button>
            <button
              className="billing-btn"
              style={{
                ...styles.billingBtn,
                ...(billingInterval === "year" ? styles.billingBtnActive : {}),
              }}
              onClick={() => setBillingInterval("year")}
            >
              Annual
              <span style={{ ...styles.billingBadge, ...styles.badgeSave }}>Save 17%</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Tab Navigation */}
          <div style={styles.tabNav}>
            {["plans", "compare", "testimonials"].map((tab) => (
              <button
                key={tab}
                className="tab-btn"
                style={{
                  ...styles.tabBtn,
                  ...(activeTab === tab ? styles.tabBtnActive : {}),
                }}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "plans" ? "Choose Plan" : tab === "compare" ? "Compare Plans" : "Testimonials"}
              </button>
            ))}
          </div>

          {/* Plans Tab */}
          {activeTab === "plans" && (
            <>
              {loading ? (
                <div style={styles.loading}>
                  <div style={styles.spinner} />
                  <span style={{ color: "rgba(255, 255, 255, 0.6)" }}>Loading plans...</span>
                </div>
              ) : (
                <>
                  <div style={styles.plansGrid}>
                    {plans.map((plan) => {
                      const isPopular = isPopularPlan(plan);
                      const isSelected = selectedPlan === plan._id;
                      const isHovered = hoveredCard === plan._id;

                      return (
                        <div
                          key={plan._id}
                          className="plan-card-hover"
                          style={{
                            ...styles.planCard,
                            ...(isPopular ? styles.planCardPopular : {}),
                            ...(isSelected ? styles.planCardSelected : {}),
                            ...(isHovered ? styles.planCardHover : {}),
                          }}
                          onClick={() => setSelectedPlan(plan._id)}
                          onMouseEnter={() => setHoveredCard(plan._id)}
                          onMouseLeave={() => setHoveredCard(null)}
                        >
                          {isPopular && (
                            <div style={styles.popularBadge}>
                              RECOMMENDED
                            </div>
                          )}

                          <div style={{ ...styles.planHeader, paddingTop: isPopular ? "38px" : "16px" }}>
                            <div style={{ ...styles.planIcon, ...(isPopular ? styles.planIconPopular : {}) }}>
                              {getPlanIcon(plan.name)}
                            </div>
                            <h3 style={styles.planName}>{plan.name}</h3>
                          </div>

                          <div style={styles.planPriceSection}>
                            <p style={{ ...styles.priceAmount, ...(isPopular ? styles.priceAmountPopular : {}) }}>
                              {formatPrice(plan)}
                            </p>
                            {plan.name !== "Free" && plan.name !== "Enterprise" && (
                              <>
                                <p style={styles.pricePeriod}>per {getPeriodText()}</p>
                                {billingInterval === "year" && (
                                  <p style={styles.priceSaveNote}>Save 2 months</p>
                                )}
                                {billingInterval === "week" && (
                                  <p style={styles.priceSaveNote}>Try before committing</p>
                                )}
                              </>
                            )}
                          </div>

                          <div style={styles.divider} />

                          <div className="features-scroll" style={styles.featuresList}>
                            {(plan.featureList?.slice(0, 6) || []).map((feature, idx) => (
                              <div key={idx} style={styles.featureItem}>
                                <div style={styles.featureCheck}>
                                  <CheckIcon />
                                </div>
                                <span>{feature}</span>
                              </div>
                            ))}
                            {(plan.featureList?.length || 0) > 6 && (
                              <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.4)", textAlign: "center", margin: "8px 0 0" }}>
                                +{plan.featureList.length - 6} more features
                              </p>
                            )}
                          </div>

                          <div style={styles.ctaSection}>
                            <button
                              className="cta-btn"
                              style={{
                                ...styles.ctaButton,
                                ...(plan.name === "Free" ? styles.ctaFree :
                                    plan.name === "Enterprise" ? styles.ctaEnterprise :
                                    styles.ctaPrimary),
                                opacity: subscribingPlan === plan._id ? 0.7 : 1,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubscribe(plan);
                              }}
                              disabled={subscribingPlan === plan._id}
                            >
                              {subscribingPlan === plan._id ? "Processing..." :
                                plan.name === "Free" ? "Start Free" :
                                plan.name === "Enterprise" ? "Contact Sales" :
                                "Subscribe Now"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Guarantees */}
                  <div style={styles.guarantees}>
                    <div style={styles.guarantee}>
                      <div style={{ ...styles.guaranteeDot, background: "#7B61FF", boxShadow: "0 0 8px rgba(123, 97, 255, 0.5)" }} />
                      <span>7-Day Money Back</span>
                    </div>
                    <div style={styles.guarantee}>
                      <div style={{ ...styles.guaranteeDot, background: "#10b981", boxShadow: "0 0 8px rgba(16, 185, 129, 0.5)" }} />
                      <span>Cancel Anytime</span>
                    </div>
                    <div style={styles.guarantee}>
                      <div style={{ ...styles.guaranteeDot, background: "#0EA5E9", boxShadow: "0 0 8px rgba(14, 165, 233, 0.5)" }} />
                      <span>Secure Payment</span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Compare Tab */}
          {activeTab === "compare" && (
            <div style={styles.comparisonTable}>
              <div style={styles.comparisonHeader}>
                <div style={{ ...styles.comparisonHeaderCell, textAlign: "left", paddingLeft: "20px" }}>Feature</div>
                {["Free", "Starter", "Pro", "Business", "Enterprise"].map((name) => (
                  <div key={name} style={styles.comparisonHeaderCell}>{name}</div>
                ))}
              </div>

              {comparisonData.map((section, sIdx) => (
                <React.Fragment key={sIdx}>
                  <div className="comparison-row" style={{ ...styles.comparisonRow, background: "rgba(255, 255, 255, 0.02)" }}>
                    <div style={{ ...styles.comparisonCell, ...styles.comparisonFeatureCell, ...styles.comparisonCategory }}>
                      {section.category}
                    </div>
                    {[...Array(5)].map((_, i) => (
                      <div key={i} style={styles.comparisonCell} />
                    ))}
                  </div>
                  {section.features.map((feature, fIdx) => (
                    <div className="comparison-row" key={fIdx} style={styles.comparisonRow}>
                      <div style={{ ...styles.comparisonCell, ...styles.comparisonFeatureCell }}>
                        {feature.name}
                      </div>
                      {feature.values.map((val, vIdx) => (
                        <div key={vIdx} style={styles.comparisonCell}>
                          {val === true ? (
                            <span style={styles.checkIcon}>✓</span>
                          ) : val === false ? (
                            <span style={styles.crossIcon}>✕</span>
                          ) : (
                            val
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Testimonials Tab */}
          {activeTab === "testimonials" && (
            <>
              <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#ffffff", marginBottom: "8px" }}>
                  Loved by 50,000+ Users Worldwide
                </h3>
                <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.6)", maxWidth: "400px", margin: "0 auto" }}>
                  Join professionals who boost their productivity with Copilotly's AI copilots.
                </p>
              </div>

              <div style={styles.statsGrid}>
                {[
                  { num: "98%", label: "Customer Satisfaction", gradient: "linear-gradient(135deg, #7B61FF 0%, #A78BFA 100%)" },
                  { num: "50K+", label: "Active Users", gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)" },
                  { num: "24/7", label: "AI Assistance", gradient: "linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)" },
                ].map((stat, idx) => (
                  <div key={idx} style={{ ...styles.statCard, background: stat.gradient }}>
                    <div style={styles.statNumber}>{stat.num}</div>
                    <div style={styles.statLabel}>{stat.label}</div>
                  </div>
                ))}
              </div>

              <div style={styles.testimonialsGrid}>
                {testimonials.map((t, idx) => (
                  <div key={idx} className="testimonial-card" style={styles.testimonialCard}>
                    <div style={styles.testimonialQuote}>"</div>
                    <p style={styles.testimonialText}>
                      {t.text.split(t.highlight).map((part, i, arr) => (
                        <React.Fragment key={i}>
                          {part}
                          {i < arr.length - 1 && <span style={styles.testimonialHighlight}>{t.highlight}</span>}
                        </React.Fragment>
                      ))}
                    </p>
                    <div style={styles.testimonialAuthor}>
                      <div style={{ ...styles.testimonialAvatar, background: t.gradient }}>
                        {t.initials}
                      </div>
                      <div>
                        <div style={styles.testimonialName}>{t.author}</div>
                        <div style={styles.testimonialRole}>{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            className="later-btn"
            style={styles.laterBtn}
            onClick={handleDecideLater}
            disabled={loading}
          >
            I'll decide later
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
